import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { consumeReturnTo } from "../lib/returnTo";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null); // {challenge_id, email_masked}
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const nav = useNavigate();
  const { login, verify2fa, register, user } = useAuth();

  useEffect(() => { if (user) nav(consumeReturnTo("/app"), { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await login(email, password);
        if (res && res.twofa_required) {
          setTwofa({ challenge_id: res.challenge_id, email_masked: res.email_masked, delivery: res.delivery, delivery_error: res.delivery_error });
          if (res.delivery === "none") { toast.error("Le code n'a pas pu être envoyé. Utilise un code de secours."); setUseBackup(true); }
          else if (res.delivery === "sms") toast.info("Un code de vérification t'a été envoyé par SMS.");
          else toast.info("Un code de vérification t'a été envoyé par courriel.");
          setBusy(false);
          return;
        }
      } else {
        await register(email, password, fullName);
      }
      toast.success("Bienvenue !");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verify2fa(twofa.challenge_id, code.trim());
      toast.success("Bienvenue !");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code invalide");
    } finally { setBusy(false); }
  };

  const fillDemo = () => { setEmail("demo@assistant.pro"); setPassword("Demo1234!"); setMode("login"); };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <div className="hard-border-r p-6 sm:p-10 pb-[max(7rem,calc(env(safe-area-inset-bottom)+6rem))] lg:pb-10 flex flex-col justify-between">
        <Link to="/" className="inline-flex items-center gap-2 ticker text-muted-foreground hover:text-foreground" data-testid="back-home">
          <ArrowLeft size={14}/> Retour
        </Link>
        <div className="max-w-md">
          <Logo product />
          <h1 className="mt-10 font-display font-black text-4xl lg:text-5xl tracking-tight">
            {mode === "login" ? "Bon retour." : "Commence ici."}
          </h1>
          <p className="mt-4 text-muted-foreground text-lg">
            {mode === "login"
              ? "Retrouve tes courriels, finances, marketing et athlètes — tout au même endroit."
              : "Crée ton espace pour piloter ton entreprise et le Circuit Québec."}
          </p>

          {twofa && (
            <form onSubmit={submitCode} className="mt-10 space-y-5" data-testid="twofa-form">
              <div className="hard-border bg-white p-4">
                <div className="ticker text-muted-foreground text-xs">Vérification en deux étapes</div>
                {twofa.delivery === "none" ? (
                  <p className="mt-2 text-sm text-red-700" data-testid="twofa-delivery-error">
                    Le code n'a pas pu être envoyé (courriel/SMS indisponibles{twofa.delivery_error ? ` : ${twofa.delivery_error}` : ""}). Utilise un <strong>code de secours</strong> pour continuer.
                  </p>
                ) : (
                  <p className="mt-2 text-sm">Un code à 6 chiffres a été envoyé {twofa.delivery === "sms" ? "par SMS au numéro de sécurité" : <>à <strong>{twofa.email_masked}</strong></>}. Saisis-le pour continuer.</p>
                )}
              </div>
              <div>
                <label className="ticker text-muted-foreground block mb-2">{useBackup ? "Code de secours" : "Code de vérification"}</label>
                {useBackup ? (
                  <input data-testid="twofa-backup-input" required value={code}
                    onChange={(e)=>setCode(e.target.value.toUpperCase())}
                    className="w-full hard-border bg-white px-4 py-3 text-center text-lg tracking-widest font-mono focus:border-foreground outline-none transition-colors" placeholder="XXXX-XXXX" />
                ) : (
                  <input data-testid="twofa-code-input" inputMode="numeric" maxLength={6} required value={code}
                    onChange={(e)=>setCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full hard-border bg-white px-4 py-3 text-center text-2xl tracking-[10px] font-mono focus:border-foreground outline-none transition-colors" placeholder="000000" />
                )}
              </div>
              <button data-testid="twofa-submit-btn" disabled={busy || (useBackup ? code.trim().length < 4 : code.length !== 6)}
                className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white py-3.5 ticker transition-colors disabled:opacity-60">
                {busy ? "…" : "Vérifier et se connecter"}
              </button>
              <button type="button" onClick={()=>{ setUseBackup(!useBackup); setCode(""); }} className="w-full ticker text-muted-foreground hover:text-foreground text-xs" data-testid="twofa-backup-toggle">
                {useBackup ? "← Utiliser le code reçu par courriel" : "Je n'ai pas accès à mes courriels — utiliser un code de secours"}
              </button>
              <button type="button" onClick={()=>{ setTwofa(null); setCode(""); }} className="w-full ticker text-muted-foreground hover:text-foreground" data-testid="twofa-cancel-btn">
                ← Annuler
              </button>
            </form>
          )}

          {!twofa && (<>
          <form onSubmit={submit} className="mt-10 space-y-5">
            {mode === "register" && (
              <div>
                <label className="ticker text-muted-foreground block mb-2">Nom complet</label>
                <input data-testid="register-fullname-input" required value={fullName} onChange={(e)=>setFullName(e.target.value)}
                  className="w-full hard-border bg-white px-4 py-3 focus:border-foreground outline-none transition-colors" placeholder="Prénom Nom" />
              </div>
            )}
            <div>
              <label className="ticker text-muted-foreground block mb-2">Courriel</label>
              <input data-testid="auth-email-input" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="w-full hard-border bg-white px-4 py-3 focus:border-foreground outline-none transition-colors" placeholder="toi@entreprise.com"/>
            </div>
            <div>
              <label className="ticker text-muted-foreground block mb-2">Mot de passe</label>
              <input data-testid="auth-password-input" type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)}
                className="w-full hard-border bg-white px-4 py-3 focus:border-foreground outline-none transition-colors" placeholder="••••••••"/>
            </div>
            <button data-testid="auth-submit-btn" disabled={busy}
              className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white py-3.5 ticker transition-colors disabled:opacity-60">
              {busy ? "…" : mode === "login" ? "Connexion" : "Créer mon compte"}
            </button>
          </form>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-black/10" />
            <span className="ticker text-muted-foreground text-xs">ou</span>
            <div className="flex-1 h-px bg-black/10" />
          </div>

          <button data-testid="google-login-btn" onClick={googleLogin} type="button"
            className="mt-4 w-full hard-border bg-white hover:bg-zinc-50 text-foreground py-3.5 ticker transition-colors flex items-center justify-center gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.8-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z"/>
              <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1C3.4 21.3 7.4 24 12 24z"/>
              <path fill="#FBBC05" d="M5.4 14.4c-.2-.7-.4-1.4-.4-2.4s.1-1.7.4-2.4V6.5H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.5l4-3.1z"/>
              <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.7 1.4 6.5l4 3.1C6.3 6.8 8.9 4.8 12 4.8z"/>
            </svg>
            Continuer avec Google
          </button>

          <div className="mt-6 flex items-center justify-between ticker">
            <button onClick={()=>setMode(mode === "login" ? "register" : "login")} className="text-muted-foreground hover:text-foreground" data-testid="toggle-mode-btn">
              {mode === "login" ? "Créer un compte →" : "← J'ai déjà un compte"}
            </button>
            <button onClick={fillDemo} className="text-[#D90429] hover:underline" data-testid="fill-demo-btn">Utiliser la démo</button>
          </div>
          </>)}
        </div>
        <div className="ticker text-muted-foreground mt-8 pl-24 max-lg:pl-0 max-lg:text-center max-lg:text-[10px]">© 2026 DOJOVA — Kaizen System · Institut Impact Élite Martial</div>
      </div>

      <div className="hidden lg:block relative grid-bg">
        <div className="absolute inset-0 flex items-center justify-center p-10">
          <div className="max-w-sm">
            <div className="ticker text-muted-foreground mb-3">— Ton écosystème</div>
            <div className="space-y-4 font-display font-bold text-3xl tracking-tight">
              <div>Courriels.</div>
              <div className="text-[#D90429]">Finances.</div>
              <div>Marketing.</div>
              <div className="underline decoration-[#D90429] decoration-2 underline-offset-[8px]">Circuit Québec.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
